<script setup>
import { ref, computed, watch } from 'vue';
import MediaCard from './components/MediaCard.vue';
import MediaFilter from './components/MediaFilter.vue';
import { onClickOutside } from '@vueuse/core';
import { useTemplateRef } from 'vue';

const props = defineProps({
	title: {
		type: String,
	},
	items: {
		type: Array,
	},
	// filters: {
	//     type: Object,
	// },
	mediaTypes: {
		type: Array,
	},
	selectedFilters: {
		type: Object,
		default: () => ({}),
	},
});

const mediaTypes = computed(() => {
	return [...new Set(props.items.map((item) => item.type))];
});

const filters = computed(() => {
	const keys = props.items.map((item) => Object.keys(item.filterValues));
	const uniqueKeys = [...new Set(keys)];

	const result = {};

	uniqueKeys.forEach((key) => {
		const values = props.items.map((item) => item.filterValues[key]).flat();
		const uniqueValues = [...new Set(values)].filter((x) => x);
		result[key] = uniqueValues;
	});
	return result;
});
const selectedFilters = ref(props.selectedFilters);
const selectedMediaType = ref([]);
const toggle = ref(false);
const expand = ref(false);

const filtersBox = useTemplateRef('filtersBox');

onClickOutside(filtersBox, (event) => {
	toggle.value = false;
});

const toggleFilters = () => {
	toggle.value = !toggle.value;
};

const expandFilter = () => {
	expand.value = !expand.value;
};

// watch(filters, () => {
for (const key of Object.keys(filters.value)) {
	if (!selectedFilters.value[key]) {
		selectedFilters.value[key] = [];
	}
}
// })

const filteredItems = computed(() => {
	return props.items.filter((item) => {
		const selectedTermsArray = Object.entries(selectedFilters.value).flatMap(([category, slugs]) =>
			slugs.map((slug) => ({ category, slug }))
		);

		const matchesMediaType = selectedMediaType.value.length === 0 || selectedMediaType.value.includes(item.type);

		if (selectedTermsArray.length === 0 && matchesMediaType) {
			return true;
		}

		const matchesSelectedTerms =
			selectedTermsArray.length === 0 ||
			Object.entries(item.filterValues).some(([category, slugs]) =>
				slugs.some((slug) =>
					selectedTermsArray.some(
						(selectedTerm) => selectedTerm.category === category && selectedTerm.slug === slug
					)
				)
			);

		return matchesSelectedTerms && matchesMediaType;
	});
});

const filteredMediaTypes = computed(() => {
	return {
		images: filteredItems.value.filter((x) => x.type === 'image'),
		videos: filteredItems.value.filter((x) => x.type === 'video'),
		documents: filteredItems.value.filter((x) => x.type === 'document'),
	};
});
</script>

<template>
	<section class="section m-page-title">
		<div class="section__inner">
			<div class="m-page-title__inner">
				<h1 class="heading-xl" v-html="title"></h1>
			</div>
		</div>
	</section>

	<section class="section">
		<div class="section__inner">
			<div class="m-locations-media__filters-box" ref="filtersBox">
				<button :class="['m-locations-media__button', { 'is-open': toggle }]" @click="toggleFilters">
					Filter media
					<svg class="icon icon-chevron">
						<use xlink:href="#icon-chevron"></use>
					</svg>
				</button>

				<div class="m-locations-media__filters" v-show="toggle">
					<div class="m-locations-media-filter-type">
						<div
							:class="['m-locations-media-filter-type__box', { 'is-open': expand }]"
							@click="expandFilter"
						>
							<h2 class="m-locations-media-filter-type__heading heading-sm">Media type</h2>
							<svg class="icon icon-chevron">
								<use xlink:href="#icon-chevron"></use>
							</svg>
						</div>
						<ul class="m-locations-media-filter-type__list" v-show="expand">
							<li class="m-locations-media-filter-type__item" v-for="type in mediaTypes" :key="type">
								<input type="checkbox" :id="type" :value="type" v-model="selectedMediaType" />
								<label :for="type">{{ type }}</label>
							</li>
						</ul>
					</div>

					<MediaFilter v-model:selectedFilters="selectedFilters" :filters="filters" />
				</div>
			</div>
		</div>
	</section>

	<section v-if="filteredMediaTypes.images.length > 0" class="section l-card-index l-card-index--mediacenter">
		<div class="section__inner">
			<div class="l-card-index__inner">
				<h2 class="l-card-index__heading heading-md">Images ({{ filteredMediaTypes.images.length }})</h2>
				<ul class="l-card-index__grid">
					<MediaCard v-for="item in filteredMediaTypes.images" :key="item.id" :item="item" />
				</ul>
			</div>
		</div>
	</section>

	<section v-if="filteredMediaTypes.videos.length > 0" class="section l-card-index l-card-index--mediacenter">
		<div class="section__inner">
			<div class="l-card-index__inner">
				<h2 class="l-card-index__heading heading-md">Videos ({{ filteredMediaTypes.videos.length }})</h2>
				<ul class="l-card-index__grid">
					<MediaCard v-for="item in filteredMediaTypes.videos" :key="item.id" :item="item" />
				</ul>
			</div>
		</div>
	</section>

	<section v-if="filteredMediaTypes.documents.length > 0" class="section l-card-index l-card-index--mediacenter">
		<div class="section__inner">
			<div class="l-card-index__inner">
				<h2 class="l-card-index__heading heading-md">Documents ({{ filteredMediaTypes.documents.length }})</h2>
				<ul class="l-card-index__grid">
					<MediaCard v-for="item in filteredMediaTypes.documents" :key="item.id" :item="item" />
				</ul>
			</div>
		</div>
	</section>
</template>
